import type { Lang } from "../types.ts";
import { t } from "../i18n/ui.ts";
import type { Tally } from "./FitRing.tsx";

interface Props {
  lang: Lang;
  tally: Tally;
}

/**
 * Progress as three stacked bars — wrong, still open, fit — the way the
 * official mobile app shows it. Each bar's width is its share of the
 * catalogue, so the three together read as one horizontal stacked total
 * without needing to be glued edge to edge.
 */
export function FitBars({ lang, tally }: Props) {
  const total = tally.total || 1;
  const rows = [
    { key: "wrong", value: tally.wrong, color: "var(--fit-red)", label: t(lang, "statsWrong") },
    {
      key: "open",
      value: tally.unseen + tally.shaky,
      color: "var(--fit-amber)",
      label: t(lang, "statsUnseen"),
    },
    { key: "correct", value: tally.correct, color: "var(--fit-green)", label: t(lang, "statsCorrect") },
  ];

  const percent = Math.round((tally.correct / total) * 100);

  return (
    <div className="fitbars">
      <div
        className="fitbars__track"
        role="img"
        aria-label={t(lang, "statsRingLabel", {
          percent,
          correct: tally.correct,
          wrong: tally.wrong,
          unseen: tally.unseen + tally.shaky,
          total: tally.total,
        })}
      >
        {rows.map((row) => (
          <span
            key={row.key}
            className="fitbars__bar"
            style={{
              inlineSize: `${Math.max(row.value > 0 ? 1.5 : 0, (row.value / total) * 100)}%`,
              background: row.color,
            }}
          />
        ))}
      </div>

      <ul className="fitbars__legend">
        {rows.map((row) => (
          <li key={row.key}>
            <span className="fitbars__dot" style={{ background: row.color }} aria-hidden="true" />
            {row.label} {row.value}
          </li>
        ))}
      </ul>
    </div>
  );
}
